import React, { useState, useEffect } from 'react';
import './AdminDebugPanel.css';
import MapsTestPage from './MapsTestPage';

const AdminDebugPanel = ({ user, DatabaseTestComponent, isOpen, onClose, onOpenFeedback }) => {
  const [activeTab, setActiveTab] = useState('logs');
  const [logs, setLogs] = useState([]);
  const [systemInfo, setSystemInfo] = useState({});
  const [apiCalls, setApiCalls] = useState([]);
  const [userActions, setUserActions] = useState([]);
  const [appState, setAppState] = useState({});

  // Initialize system info
  useEffect(() => {
    setSystemInfo({
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine
    });

    // Add initial log entry
    addLog('Admin Debug Panel initialized', 'system', {
      user: user?.email || 'Anonymous',
      timestamp: new Date().toISOString()
    });
  }, [user]);

  const addLog = (message, type = 'info', data = null) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      message,
      type, // 'info', 'warning', 'error', 'system', 'api', 'user'
      data
    };
    setLogs(prev => [logEntry, ...prev].slice(0, 100)); // Keep last 100 logs
  };

  const addApiCall = (url, method, request, response, duration) => {
    const apiCall = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      url,
      method,
      request,
      response,
      duration,
      status: response?.status || 'pending'
    };
    setApiCalls(prev => [apiCall, ...prev].slice(0, 50)); // Keep last 50 API calls
  };

  const addUserAction = (action, details) => {
    const userAction = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      action,
      details,
      user: user?.email || 'Anonymous'
    };
    setUserActions(prev => [userAction, ...prev].slice(0, 50)); // Keep last 50 actions
  };

  // Expose methods globally for other components to use
  useEffect(() => {
    window.adminDebug = {
      addLog,
      addApiCall,
      addUserAction,
      setAppState: (newState) => setAppState(prev => ({ ...prev, ...newState }))
    };

    return () => {
      delete window.adminDebug;
    };
  }, []);

  const clearLogs = () => {
    setLogs([]);
    addLog('Logs cleared by admin', 'system');
  };

  const exportLogs = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      logs,
      apiCalls,
      userActions,
      systemInfo,
      appState,
      user: user ? {
        email: user.email,
        name: user.name,
        id: user.sub
      } : null
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-export-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    addLog('Debug data exported', 'system');
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getLogTypeIcon = (type) => {
    switch (type) {
      case 'error': return '🔴';
      case 'warning': return '🟡';
      case 'api': return '🌐';
      case 'user': return '👤';
      case 'system': return '⚙️';
      default: return '💙';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="admin-debug-overlay">
      <div className="admin-debug-panel">
        <div className="admin-debug-header">
          <div className="header-left">
            <h2>🛠️ Admin Debug Panel</h2>
            <div className="current-version-indicator">
              Currently Running: <span className="version-badge">V2 - Integrated</span>
            </div>
          </div>
          <div className="admin-debug-actions">
            <button onClick={onOpenFeedback} className="btn-debug btn-feedback">
              💬 Feedback
            </button>
            <button onClick={exportLogs} className="btn-debug btn-export">
              📁 Export
            </button>
            <button onClick={onClose} className="btn-debug btn-close">
              ✕
            </button>
          </div>
        </div>

        <div className="admin-debug-tabs">
          <button 
            className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 Logs ({logs.length})
          </button>
          <button 
            className={`tab ${activeTab === 'api' ? 'active' : ''}`}
            onClick={() => setActiveTab('api')}
          >
            🌐 API Calls ({apiCalls.length})
          </button>
          <button 
            className={`tab ${activeTab === 'user' ? 'active' : ''}`}
            onClick={() => setActiveTab('user')}
          >
            👤 User Actions ({userActions.length})
          </button>
          <button 
            className={`tab ${activeTab === 'state' ? 'active' : ''}`}
            onClick={() => setActiveTab('state')}
          >
            📊 App State
          </button>
          <button 
            className={`tab ${activeTab === 'system' ? 'active' : ''}`}
            onClick={() => setActiveTab('system')}
          >
            ⚙️ System Info
          </button>
          <button 
            className={`tab ${activeTab === 'maps' ? 'active' : ''}`}
            onClick={() => setActiveTab('maps')}
          >
            🗺️ Maps Test
          </button>
          <button 
            className={`tab ${activeTab === 'database' ? 'active' : ''}`}
            onClick={() => setActiveTab('database')}
          >
            🗄️ Database Test
          </button>
        </div>

        <div className="admin-debug-content">
          {activeTab === 'logs' && (
            <div className="logs-panel">
              <div className="panel-header">
                <h3>Application Logs</h3>
                <button onClick={clearLogs} className="btn-debug btn-clear">
                  🗑️ Clear
                </button>
              </div>
              <div className="logs-container">
                {logs.map(log => (
                  <div key={log.id} className={`log-entry log-${log.type}`}>
                    <div className="log-header">
                      <span className="log-icon">{getLogTypeIcon(log.type)}</span>
                      <span className="log-time">{formatTimestamp(log.timestamp)}</span>
                      <span className="log-type">{log.type.toUpperCase()}</span>
                    </div>
                    <div className="log-message">{log.message}</div>
                    {log.data && (
                      <details className="log-data">
                        <summary>View Data</summary>
                        <pre>{JSON.stringify(log.data, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                ))}
                {logs.length === 0 && (
                  <div className="empty-state">No logs yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'api' && (
            <div className="api-panel">
              <div className="panel-header">
                <h3>API Calls</h3>
              </div>
              <div className="api-container">
                {apiCalls.map(call => (
                  <div key={call.id} className="api-entry">
                    <div className="api-header">
                      <span className={`api-method method-${call.method.toLowerCase()}`}>
                        {call.method}
                      </span>
                      <span className="api-url">{call.url}</span>
                      <span className="api-time">{formatTimestamp(call.timestamp)}</span>
                      <span className={`api-status status-${Math.floor(call.status / 100)}`}>
                        {call.status}
                      </span>
                    </div>
                    <div className="api-details">
                      <details>
                        <summary>Request</summary>
                        <pre>{JSON.stringify(call.request, null, 2)}</pre>
                      </details>
                      <details>
                        <summary>Response</summary>
                        <pre>{JSON.stringify(call.response, null, 2)}</pre>
                      </details>
                    </div>
                  </div>
                ))}
                {apiCalls.length === 0 && (
                  <div className="empty-state">No API calls yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'user' && (
            <div className="user-panel">
              <div className="panel-header">
                <h3>User Actions</h3>
              </div>
              <div className="user-container">
                {userActions.map(action => (
                  <div key={action.id} className="user-entry">
                    <div className="user-header">
                      <span className="user-icon">👤</span>
                      <span className="user-action">{action.action}</span>
                      <span className="user-time">{formatTimestamp(action.timestamp)}</span>
                    </div>
                    <div className="user-details">
                      <div className="user-user">User: {action.user}</div>
                      {action.details && (
                        <details>
                          <summary>Details</summary>
                          <pre>{JSON.stringify(action.details, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
                {userActions.length === 0 && (
                  <div className="empty-state">No user actions yet</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'state' && (
            <div className="state-panel">
              <div className="panel-header">
                <h3>Application State</h3>
              </div>
              <div className="state-container">
                <div className="state-section">
                  <h4>Current User</h4>
                  <pre>{JSON.stringify(user, null, 2)}</pre>
                </div>
                <div className="state-section">
                  <h4>App State</h4>
                  <pre>{JSON.stringify(appState, null, 2)}</pre>
                </div>
                <div className="state-section">
                  <h4>Local Storage</h4>
                  <pre>{JSON.stringify(Object.fromEntries(
                    Object.entries(localStorage).map(([k, v]) => [k, v])
                  ), null, 2)}</pre>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="system-panel">
              <div className="panel-header">
                <h3>System Information</h3>
              </div>
              <div className="system-container">
                {Object.entries(systemInfo).map(([key, value]) => (
                  <div key={key} className="system-field">
                    <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong>
                    <span>{value?.toString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'maps' && (
            <MapsTestPage />
          )}

          {activeTab === 'database' && DatabaseTestComponent && (
            <div className="database-panel">
              <div className="panel-header">
                <h3>Database Test Suite</h3>
              </div>
              <div className="database-container">
                <DatabaseTestComponent user={user} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDebugPanel; 